import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, CheckCircle, Trophy,
  UtensilsCrossed, Cloud, Sun, CloudRain, CloudSnow, Wind,
  Home, Check, ShieldCheck, CircleDot, Loader, Monitor,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { apiClient } from '../utils/api';
import { format, isToday, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths, startOfDay, isSameDay } from 'date-fns';
import { parseDate, formatDate, parseLocalDate, formatTimeString, formatRelativeDate } from '../utils/dateUtils';
import { getConfiguredTimezone } from '../utils/timezoneUtils';
import { useTVNavigation } from '@/hooks/useTVNavigation';
import { APP_VERSION } from '@/version';
import { useNavigate } from 'react-router-dom';
import { CookieUtils, KIOSK_COOKIES } from '@/utils/cookies';
import '@/styles/tv-kiosk.css';

interface KioskConfig {
  family_name?: string;
  family_slug?: string;
  kiosk_type?: string;
  features?: Record<string, boolean>;
}

interface Chore {
  id: string;
  chore_name: string;
  assigned_to: string;
  status: 'pending' | 'completed' | 'verified';
  points_offered: number;
  due_date?: string;
  icon?: string;
}

interface ChoresSummary {
  chores: Chore[];
  completed_count: number;
  pending_count: number;
  verified_count: number;
}

interface TVKioskDashboardProps {
  config?: KioskConfig;
}

type WidgetView = 'chores' | 'leaderboard' | 'meals' | 'calendar';
type CalendarViewMode = 'day' | 'week' | 'month' | 'agenda';
type LeaderboardViewMode = 'day' | 'week' | 'month' | 'all';
type MealsViewMode = 'today' | 'week';

/**
 * TV Kiosk Dashboard
 *
 * Optimized for 10-foot UI viewing on smart TVs.
 * Features:
 * - Large, readable fonts
 * - D-pad navigation support
 * - Auto-rotating widget carousel
 * - Simplified layout for TV screens
 */
export const TVKioskDashboard: React.FC<TVKioskDashboardProps> = ({ config }) => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [choresSummary, setChoresSummary] = useState<ChoresSummary | null>(null);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [dinner, setDinner] = useState<any>(null);
  const [weekMeals, setWeekMeals] = useState<any[]>([]);
  const [presence, setPresence] = useState<any[]>([]);
  const [weather, setWeather] = useState<{
    temperature: number;
    condition: string;
    high: number;
    low: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeWidget, setActiveWidget] = useState<WidgetView>('chores');
  const [autoRotate, setAutoRotate] = useState(true);
  const [calendarView, setCalendarView] = useState<CalendarViewMode>('week');
  const [leaderboardView, setLeaderboardView] = useState<LeaderboardViewMode>('week');
  const [mealsView, setMealsView] = useState<MealsViewMode>('week');
  const [calendarOffset, setCalendarOffset] = useState(0); // 0 = current, -1 = previous, +1 = next
  const [leaderboardOffset, setLeaderboardOffset] = useState(0);

  // Available widgets based on config
  const availableWidgets: WidgetView[] = ['chores', 'leaderboard', 'meals', 'calendar'].filter(w => {
    if (!config?.features) return true;
    const featureMap: Record<string, string> = {
      chores: 'kiosk_chores',
      leaderboard: 'kiosk_leaderboard',
      meals: 'kiosk_meals',
      calendar: 'kiosk_calendar'
    };
    return config.features[featureMap[w]] !== false;
  }) as WidgetView[];

  // TV Navigation - enables D-pad navigation for smart TVs
  useTVNavigation({
    onBack: () => {
      // Stop auto-rotate on back press
      setAutoRotate(false);
    }
  });

  // Handle TV back button
  useEffect(() => {
    const handleBack = () => setAutoRotate(false);
    window.addEventListener('tv-back', handleBack);
    return () => window.removeEventListener('tv-back', handleBack);
  }, []);

  // Check for kiosk device token
  useEffect(() => {
    const localToken = localStorage.getItem('kiosk_device_token');
    const cookieToken = CookieUtils.getCookie(KIOSK_COOKIES.DEVICE_TOKEN);

    if (cookieToken && !localToken) {
      localStorage.setItem('kiosk_device_token', cookieToken);
    }

    if (!localToken && !cookieToken) {
      navigate('/kiosk/activate');
    }
  }, [navigate]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Auto-rotate widgets every 30 seconds
  useEffect(() => {
    if (!autoRotate || availableWidgets.length <= 1) return;

    const timer = setInterval(() => {
      setActiveWidget(prev => {
        const currentIndex = availableWidgets.indexOf(prev);
        const nextIndex = (currentIndex + 1) % availableWidgets.length;
        return availableWidgets[nextIndex];
      });
    }, 30000);

    return () => clearInterval(timer);
  }, [autoRotate, availableWidgets]);

  // Weather icon helper
  const getWeatherIcon = (condition: string) => {
    const lowerCondition = condition.toLowerCase();
    if (lowerCondition.includes('rain')) return <CloudRain className="tv-weather-icon text-blue-300" />;
    if (lowerCondition.includes('snow')) return <CloudSnow className="tv-weather-icon text-blue-100" />;
    if (lowerCondition.includes('clear') || lowerCondition.includes('sunny')) return <Sun className="tv-weather-icon text-yellow-300" />;
    if (lowerCondition.includes('cloud')) return <Cloud className="tv-weather-icon text-gray-300" />;
    if (lowerCondition.includes('wind')) return <Wind className="tv-weather-icon text-gray-300" />;
    return <Sun className="tv-weather-icon text-yellow-300" />;
  };

  // Fetch weather
  const fetchWeather = useCallback(async () => {
    try {
      const response = await apiClient.getKioskWeather();
      if (response) {
        setWeather({
          temperature: response.temperature || 75,
          condition: response.condition || 'Clear',
          high: response.high || 82,
          low: response.low || 65
        });
      }
    } catch (err) {
      console.error('Error fetching weather:', err);
    }
  }, []);

  // Fetch leaderboard for a specific period and offset
  const fetchLeaderboard = useCallback(async (period: LeaderboardViewMode = 'week', offset: number = 0) => {
    try {
      // Map view mode to API period parameter
      const periodMap: Record<LeaderboardViewMode, string> = {
        day: 'today',
        week: 'weekly',
        month: 'monthly',
        all: 'alltime'
      };
      const apiPeriod = periodMap[period];

      // Build URL with optional offset parameter
      const url = offset !== 0 && period !== 'all'
        ? `/api/kiosk/leaderboard/${apiPeriod}?offset=${offset}`
        : `/api/kiosk/leaderboard/${apiPeriod}`;

      const response = await apiClient.get(url);
      // Handle both array and object responses
      if (Array.isArray(response)) {
        setLeaderboard(response);
      } else if (response?.leaderboard && Array.isArray(response.leaderboard)) {
        setLeaderboard(response.leaderboard);
      } else if (response?.children && Array.isArray(response.children)) {
        setLeaderboard(response.children);
      } else {
        setLeaderboard([]);
      }
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setLeaderboard([]);
    }
  }, []);

  // Load kiosk data
  const loadKioskData = useCallback(async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) setLoading(true);
      setError(null);

      const [statusRes, calendarRes] = await Promise.allSettled([
        apiClient.getKioskStatus(),
        apiClient.get('/api/kiosk/external-calendar-events', {
          params: {
            start_date: format(startOfWeek(new Date()), 'yyyy-MM-dd'),
            end_date: format(endOfWeek(new Date()), 'yyyy-MM-dd')
          }
        })
      ]);

      if (statusRes.status === 'fulfilled') {
        const status = statusRes.value;
        setChoresSummary(status.chores_summary || null);
        setDinner(status.dinner || null);
        setWeekMeals(status.week_meals || []);
        setPresence(status.presence || []);
      }

      if (calendarRes.status === 'fulfilled') {
        // Handle both array and object responses
        const events = calendarRes.value;
        if (Array.isArray(events)) {
          setCalendarEvents(events);
        } else if (events?.events && Array.isArray(events.events)) {
          setCalendarEvents(events.events);
        } else {
          setCalendarEvents([]);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading kiosk data:', err);
      setError('Failed to load kiosk data');
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadKioskData(true);
    fetchLeaderboard(leaderboardView);
    fetchWeather();
  }, [loadKioskData, fetchLeaderboard, fetchWeather]);

  // Refetch leaderboard when view or offset changes
  useEffect(() => {
    fetchLeaderboard(leaderboardView, leaderboardOffset);
  }, [leaderboardView, leaderboardOffset, fetchLeaderboard]);

  // Refresh data every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      loadKioskData(false);
      fetchLeaderboard(leaderboardView, leaderboardOffset);
    }, 120000);
    return () => clearInterval(interval);
  }, [loadKioskData, fetchLeaderboard, leaderboardView, leaderboardOffset]);

  // Refresh weather every 30 minutes
  useEffect(() => {
    const interval = setInterval(fetchWeather, 1800000);
    return () => clearInterval(interval);
  }, [fetchWeather]);

  // Group chores by child
  const choresByChild = React.useMemo(() => {
    if (!choresSummary?.chores) return {};
    return choresSummary.chores.reduce((acc, chore) => {
      const child = chore.assigned_to;
      if (!acc[child]) acc[child] = [];
      acc[child].push(chore);
      return acc;
    }, {} as Record<string, Chore[]>);
  }, [choresSummary]);

  // Loading state
  if (loading) {
    return (
      <div className="tv-kiosk min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="tv-loading text-white">
          <div className="tv-spinner" />
          <span className="text-3xl">Loading...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="tv-kiosk min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-white text-center">
          <Monitor className="w-24 h-24 mx-auto mb-6 opacity-50" />
          <div className="text-3xl mb-4">Connection Error</div>
          <div className="text-xl opacity-70">{error}</div>
        </div>
      </div>
    );
  }

  const totalChores = choresSummary
    ? choresSummary.pending_count + choresSummary.completed_count + choresSummary.verified_count
    : 0;
  const progressPercent = totalChores > 0
    ? ((choresSummary?.completed_count || 0) + (choresSummary?.verified_count || 0)) / totalChores * 100
    : 0;

  return (
    <div className="tv-kiosk min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        {/* Time */}
        <div className="text-white">
          <div className="tv-time">
            {format(currentTime, 'h:mm')}
            <span className="tv-time-period">{format(currentTime, 'a')}</span>
          </div>
          <div className="tv-date">{format(currentTime, 'EEEE, MMMM d')}</div>
        </div>

        {/* Weather */}
        {weather && (
          <div className="tv-weather bg-white/10 rounded-2xl px-8 py-4 border border-white/20">
            {getWeatherIcon(weather.condition)}
            <div className="text-white">
              <div className="tv-weather-temp">{weather.temperature}°</div>
              <div className="text-small opacity-80">{weather.condition}</div>
            </div>
            <div className="text-white/70 text-small border-l border-white/20 pl-6 ml-2">
              <div>H: {weather.high}°</div>
              <div>L: {weather.low}°</div>
            </div>
          </div>
        )}

        {/* Family Presence - Kids at Home */}
        {Array.isArray(presence) && presence.length > 0 && (
          <div className="flex items-center gap-3 bg-white/10 rounded-2xl px-4 py-3 border border-white/20">
            <Home className="w-6 h-6 text-purple-400 flex-shrink-0" />
            <div className="flex items-center gap-2 overflow-hidden">
              {presence.slice(0, 8).map((person, idx) => (
                <div
                  key={idx}
                  className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                    person.is_present
                      ? 'bg-gradient-to-br from-green-500 to-green-600 ring-2 ring-green-400/50'
                      : 'bg-white/20 opacity-40'
                  }`}
                  title={person.child || 'Unknown'}
                >
                  {(person.child || '?').charAt(0).toUpperCase()}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Widget Navigation Tabs */}
      <div className="flex gap-4 mb-6">
        {availableWidgets.map((widget) => (
          <button
            key={widget}
            onClick={() => {
              setActiveWidget(widget);
              setAutoRotate(false);
            }}
            className={`px-8 py-4 rounded-xl font-semibold transition-all ${
              activeWidget === widget
                ? 'bg-white/20 text-white border-2 border-white/40'
                : 'bg-white/5 text-white/60 border-2 border-transparent'
            }`}
          >
            {widget === 'chores' && <CheckCircle className="inline w-6 h-6 mr-2" />}
            {widget === 'leaderboard' && <Trophy className="inline w-6 h-6 mr-2" />}
            {widget === 'meals' && <UtensilsCrossed className="inline w-6 h-6 mr-2" />}
            {widget === 'calendar' && <Calendar className="inline w-6 h-6 mr-2" />}
            {widget.charAt(0).toUpperCase() + widget.slice(1)}
          </button>
        ))}

        {/* Auto-rotate toggle */}
        <button
          onClick={() => setAutoRotate(!autoRotate)}
          className={`ml-auto px-6 py-4 rounded-xl text-small ${
            autoRotate ? 'bg-green-500/20 text-green-300' : 'bg-white/5 text-white/40'
          }`}
        >
          {autoRotate ? 'Auto-Rotating' : 'Manual'}
        </button>
      </div>

      {/* Main Content Area */}
      <div className="tv-widget flex-1">
        {/* Chores Widget */}
        {activeWidget === 'chores' && choresSummary && (
          <div>
            <div className="tv-widget-header">
              <CheckCircle className="text-green-400" />
              <span>Today's Chores</span>
              <span className="ml-auto text-white/60 text-small">
                {Math.round(progressPercent)}% complete
              </span>
            </div>

            {/* Progress Bar */}
            <div className="tv-progress-bar mb-6">
              <div className="flex h-full">
                <div
                  className="tv-progress-fill bg-blue-500"
                  style={{ width: `${totalChores > 0 ? (choresSummary.verified_count / totalChores) * 100 : 0}%` }}
                />
                <div
                  className="tv-progress-fill bg-green-500"
                  style={{ width: `${totalChores > 0 ? (choresSummary.completed_count / totalChores) * 100 : 0}%` }}
                />
              </div>
            </div>

            {/* Chores by Child */}
            <div className="tv-grid tv-grid-2 tv-scrollable" style={{ maxHeight: '60vh' }}>
              {Object.entries(choresByChild).map(([child, chores]) => {
                const childDone = chores.filter(c => c.status !== 'pending').length;
                const childTotal = chores.length;

                return (
                  <div key={child} className="bg-white/5 rounded-xl p-6 border border-white/10">
                    <div className="flex items-center mb-4">
                      <div className="tv-avatar bg-gradient-to-br from-purple-500 to-pink-500 text-white mr-4">
                        {child.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-white">{child}</div>
                        <div className="text-white/60 text-small">{childDone}/{childTotal} complete</div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {chores.slice(0, 4).map((chore) => {
                        // Format due date using centralized utilities
                        const dueDateStr = chore.due_date ? formatRelativeDate(chore.due_date) : 'Today';

                        return (
                          <div
                            key={chore.id}
                            className={`tv-list-item flex items-center justify-between ${
                              chore.status === 'verified'
                                ? 'bg-blue-500/20 border-blue-400/30'
                                : chore.status === 'completed'
                                ? 'bg-green-500/20 border-green-400/30'
                                : ''
                            }`}
                          >
                            <span className="flex items-center">
                              {chore.status === 'verified' ? (
                                <ShieldCheck className="w-6 h-6 mr-3 text-blue-400" />
                              ) : chore.status === 'completed' ? (
                                <Check className="w-6 h-6 mr-3 text-green-400" />
                              ) : (
                                <CircleDot className="w-6 h-6 mr-3 text-white/40" />
                              )}
                              <span className="flex flex-col">
                                <span className="text-white">{chore.chore_name}</span>
                                <span className={`text-sm ${
                                  dueDateStr.includes('Overdue') ? 'text-red-400' : 'text-white/50'
                                }`}>
                                  {dueDateStr}
                                </span>
                              </span>
                            </span>
                            <span className="text-yellow-400">{chore.points_offered}pts</span>
                          </div>
                        );
                      })}
                      {chores.length > 4 && (
                        <div className="text-center text-white/40 text-small">
                          +{chores.length - 4} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Leaderboard Widget */}
        {activeWidget === 'leaderboard' && (
          <div>
            <div className="tv-widget-header">
              <Trophy className="text-yellow-400" />
              <span>
                {leaderboardView === 'all' ? 'All-Time Leaderboard' : (() => {
                  const today = new Date();
                  if (leaderboardOffset === 0) {
                    if (leaderboardView === 'day') return "Today's Leaderboard";
                    if (leaderboardView === 'week') return "This Week's Leaderboard";
                    if (leaderboardView === 'month') return "This Month's Leaderboard";
                  }
                  // Show period label for offset views
                  const baseDate = leaderboardView === 'day'
                    ? addDays(today, leaderboardOffset)
                    : leaderboardView === 'week'
                    ? addWeeks(today, leaderboardOffset)
                    : addMonths(today, leaderboardOffset);

                  if (leaderboardView === 'day') {
                    return format(baseDate, 'EEEE, MMM d');
                  } else if (leaderboardView === 'week') {
                    const weekStart = startOfWeek(baseDate);
                    const weekEnd = endOfWeek(baseDate);
                    return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')}`;
                  } else {
                    return format(baseDate, 'MMMM yyyy');
                  }
                })()}
              </span>
              <div className="ml-auto flex items-center gap-2">
                {/* Navigation buttons - not shown for all-time view */}
                {leaderboardView !== 'all' && (
                  <>
                    <button
                      onClick={() => setLeaderboardOffset(o => o - 1)}
                      className="p-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 transition-all"
                      title="Previous"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setLeaderboardOffset(0)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        leaderboardOffset === 0
                          ? 'bg-yellow-500/30 text-yellow-300'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      Current
                    </button>
                    <button
                      onClick={() => setLeaderboardOffset(o => Math.min(0, o + 1))}
                      className="p-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 transition-all disabled:opacity-30"
                      title="Next"
                      disabled={leaderboardOffset >= 0}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="w-px h-6 bg-white/20 mx-2" />
                  </>
                )}
                {(['day', 'week', 'month', 'all'] as LeaderboardViewMode[]).map((view) => (
                  <button
                    key={view}
                    onClick={() => {
                      setLeaderboardView(view);
                      setLeaderboardOffset(0); // Reset offset when changing view
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      leaderboardView === view
                        ? 'bg-yellow-500/30 text-yellow-300 border border-yellow-400/50'
                        : 'bg-white/5 text-white/60 border border-transparent hover:bg-white/10'
                    }`}
                  >
                    {view === 'all' ? 'All' : view.charAt(0).toUpperCase() + view.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {(Array.isArray(leaderboard) ? leaderboard : []).slice(0, 5).map((child, idx) => (
                <div
                  key={idx}
                  tabIndex={0}
                  className="tv-list-item flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <div className={`text-4xl font-bold w-16 ${
                      idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-300' : idx === 2 ? 'text-amber-600' : 'text-white/40'
                    }`}>
                      #{idx + 1}
                    </div>
                    <div className="tv-avatar bg-gradient-to-br from-purple-500 to-pink-500 text-white mr-4">
                      {(child.display_name || child.username || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="font-semibold text-white text-3xl">
                      {child.display_name || child.username || 'Unknown'}
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-yellow-400">
                    {child.total_points || 0}
                  </div>
                </div>
              ))}
              {(!Array.isArray(leaderboard) || leaderboard.length === 0) && (
                <div className="text-center text-white/50 py-12">
                  {leaderboardOffset === 0 ? (
                    <>
                      {leaderboardView === 'day' && 'No points earned today'}
                      {leaderboardView === 'week' && 'No points earned this week'}
                      {leaderboardView === 'month' && 'No points earned this month'}
                      {leaderboardView === 'all' && 'No points earned yet'}
                    </>
                  ) : (
                    'No points earned in this period'
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Meals Widget */}
        {activeWidget === 'meals' && (
          <div>
            <div className="tv-widget-header">
              <UtensilsCrossed className="text-orange-400" />
              <span>
                {mealsView === 'today' ? "Today's Meals" : "Week's Meal Plan"}
              </span>
              <div className="ml-auto flex gap-2">
                {(['today', 'week'] as MealsViewMode[]).map((view) => (
                  <button
                    key={view}
                    onClick={() => setMealsView(view)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      mealsView === view
                        ? 'bg-orange-500/30 text-orange-300 border border-orange-400/50'
                        : 'bg-white/5 text-white/60 border border-transparent hover:bg-white/10'
                    }`}
                  >
                    {view.charAt(0).toUpperCase() + view.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Today's Meals View - shows dinner prominently with all day's meals */}
            {mealsView === 'today' && (
              <>
                {/* Tonight's Dinner */}
                {dinner && (
                  <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-xl p-8 border border-orange-400/30 mb-6">
                    <div className="text-orange-200 mb-2">Tonight's Dinner</div>
                    <div className="text-4xl text-white font-bold flex items-center">
                      {dinner.emoji && <span className="mr-4 text-5xl">{dinner.emoji}</span>}
                      {dinner.meal_name}
                    </div>
                    {dinner.cook?.name && (
                      <div className="text-orange-200 mt-2">Cook: {dinner.cook.name}</div>
                    )}
                  </div>
                )}

                {/* Today's Other Meals */}
                <div className="tv-grid tv-grid-3">
                  {(Array.isArray(weekMeals) ? weekMeals : [])
                    .filter((meal) => {
                      const mealDateStr = meal.date || meal.meal_date;
                      const mealDate = mealDateStr ? parseLocalDate(mealDateStr) : null;
                      return mealDate ? isToday(mealDate) : false;
                    })
                    .map((meal, idx) => (
                      <div key={idx} className="tv-list-item bg-orange-500/10 border-orange-400/30">
                        <div className="text-white/60 mb-2">{meal.meal_type || 'Meal'}</div>
                        <div className="text-white text-2xl flex items-center">
                          {meal.emoji && <span className="mr-3 text-3xl">{meal.emoji}</span>}
                          {meal.meal_name || 'No meal planned'}
                        </div>
                      </div>
                    ))}
                </div>

                {!dinner && (!Array.isArray(weekMeals) || weekMeals.filter((m) => {
                  const d = m.date || m.meal_date;
                  return d ? isToday(parseLocalDate(d)) : false;
                }).length === 0) && (
                  <div className="text-center text-white/50 py-12">
                    No meals planned for today
                  </div>
                )}
              </>
            )}

            {/* Week's Meals View */}
            {mealsView === 'week' && (
              <>
                {/* Tonight's Dinner at top */}
                {dinner && (
                  <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-xl p-8 border border-orange-400/30 mb-6">
                    <div className="text-orange-200 mb-2">Tonight's Dinner</div>
                    <div className="text-4xl text-white font-bold flex items-center">
                      {dinner.emoji && <span className="mr-4 text-5xl">{dinner.emoji}</span>}
                      {dinner.meal_name}
                    </div>
                    {dinner.cook?.name && (
                      <div className="text-orange-200 mt-2">Cook: {dinner.cook.name}</div>
                    )}
                  </div>
                )}

                {/* Week's Meals Grid */}
                <div className="tv-grid tv-grid-3">
                  {(Array.isArray(weekMeals) ? weekMeals : []).slice(0, 6).map((meal, idx) => {
                    const mealDateStr = meal.date || meal.meal_date;
                    const mealDate = mealDateStr ? parseLocalDate(mealDateStr) : null;
                    const dayName = mealDate ? formatDate(mealDate, 'EEEE') : 'Unknown';
                    const isMealTonight = mealDate ? isToday(mealDate) : false;

                    return (
                      <div
                        key={idx}
                        className={`tv-list-item ${
                          isMealTonight ? 'bg-orange-500/20 border-orange-400/30' : ''
                        }`}
                      >
                        <div className="text-white/60 mb-2">{dayName}</div>
                        <div className="text-white text-2xl flex items-center">
                          {meal.emoji && <span className="mr-3 text-3xl">{meal.emoji}</span>}
                          {meal.meal_name || 'No meal planned'}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {(!Array.isArray(weekMeals) || weekMeals.length === 0) && !dinner && (
                  <div className="text-center text-white/50 py-12">
                    No meals planned this week
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Calendar Widget */}
        {activeWidget === 'calendar' && (
          <div>
            <div className="tv-widget-header">
              <Calendar className="text-blue-400" />
              <span>
                {calendarView === 'agenda' ? 'Upcoming Events' : (() => {
                  const today = new Date();
                  const baseDate = calendarView === 'day'
                    ? addDays(today, calendarOffset)
                    : calendarView === 'week'
                    ? addWeeks(today, calendarOffset)
                    : addMonths(today, calendarOffset);

                  if (calendarView === 'day') {
                    return calendarOffset === 0 ? "Today's Events" : format(baseDate, 'EEEE, MMM d');
                  } else if (calendarView === 'week') {
                    const weekStart = startOfWeek(baseDate);
                    const weekEnd = endOfWeek(baseDate);
                    return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
                  } else {
                    return format(baseDate, 'MMMM yyyy');
                  }
                })()}
              </span>
              <div className="ml-auto flex items-center gap-2">
                {/* Navigation buttons - not shown for agenda view */}
                {calendarView !== 'agenda' && (
                  <>
                    <button
                      onClick={() => setCalendarOffset(o => o - 1)}
                      className="p-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 transition-all"
                      title="Previous"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCalendarOffset(0)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        calendarOffset === 0
                          ? 'bg-blue-500/30 text-blue-300'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => setCalendarOffset(o => o + 1)}
                      className="p-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 transition-all"
                      title="Next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="w-px h-6 bg-white/20 mx-2" />
                  </>
                )}
                {(['day', 'week', 'month', 'agenda'] as CalendarViewMode[]).map((view) => (
                  <button
                    key={view}
                    onClick={() => {
                      setCalendarView(view);
                      setCalendarOffset(0); // Reset offset when changing view
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      calendarView === view
                        ? 'bg-blue-500/30 text-blue-300 border border-blue-400/50'
                        : 'bg-white/5 text-white/60 border border-transparent hover:bg-white/10'
                    }`}
                  >
                    {view.charAt(0).toUpperCase() + view.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Filter events based on view and offset */}
            {(() => {
              const events = Array.isArray(calendarEvents) ? calendarEvents : [];
              const today = startOfDay(new Date());

              // Calculate the base date based on offset
              const baseDate = calendarView === 'day'
                ? addDays(today, calendarOffset)
                : calendarView === 'week'
                ? addWeeks(today, calendarOffset)
                : calendarView === 'month'
                ? addMonths(today, calendarOffset)
                : today;

              // Filter events based on selected view and offset
              const filteredEvents = events.filter((event) => {
                const eventDateStr = event.event_date || event.date || event.start;
                if (!eventDateStr) return false;
                const eventDate = parseLocalDate(eventDateStr);
                if (!eventDate) return false;

                switch (calendarView) {
                  case 'day':
                    return isSameDay(eventDate, baseDate);
                  case 'week':
                    return eventDate >= startOfWeek(baseDate) && eventDate <= endOfWeek(baseDate);
                  case 'month':
                    return eventDate >= startOfMonth(baseDate) && eventDate <= endOfMonth(baseDate);
                  case 'agenda':
                  default:
                    return eventDate >= today; // All upcoming events from today
                }
              });

              // Sort by date
              filteredEvents.sort((a, b) => {
                const dateA = parseLocalDate(a.event_date || a.date || a.start);
                const dateB = parseLocalDate(b.event_date || b.date || b.start);
                return (dateA?.getTime() || 0) - (dateB?.getTime() || 0);
              });

              if (filteredEvents.length === 0) {
                return (
                  <div className="text-center text-white/50 py-12">
                    {calendarView === 'day' && (calendarOffset === 0 ? 'No events today' : 'No events on this day')}
                    {calendarView === 'week' && 'No events this week'}
                    {calendarView === 'month' && 'No events this month'}
                    {calendarView === 'agenda' && 'No upcoming events'}
                  </div>
                );
              }

              return (
                <div className={calendarView === 'agenda' ? 'space-y-3' : 'tv-grid tv-grid-2'}>
                  {filteredEvents.slice(0, calendarView === 'agenda' ? 12 : 8).map((event, idx) => {
                    const eventDateStr = event.event_date || event.date || event.start;
                    const parsedDate = eventDateStr ? parseLocalDate(eventDateStr) : null;
                    const dateStr = parsedDate ? formatDate(parsedDate, calendarView === 'day' ? 'EEEE' : 'EEEE, MMM d') : 'No date';
                    const timeStr = event.start_time ? formatTimeString(event.start_time) : '';
                    const isEventToday = parsedDate ? isToday(parsedDate) : false;

                    return (
                      <div
                        key={idx}
                        className={`tv-list-item ${isEventToday ? 'bg-blue-500/10 border-blue-400/30' : ''}`}
                      >
                        <div className="font-semibold text-white text-2xl mb-2">
                          {event.title || event.summary || 'Untitled Event'}
                        </div>
                        <div className="text-white/70">
                          {calendarView === 'day' ? (timeStr || 'All day') : dateStr}
                          {calendarView !== 'day' && timeStr && ` at ${timeStr}`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center mt-6 text-white/40 text-small">
        <div>HousePoints v{APP_VERSION}</div>
        <div>{config?.family_name || 'Family Dashboard'}</div>
        {autoRotate && (
          <div className="flex items-center gap-2">
            <Loader className="w-4 h-4 animate-spin" />
            Auto-rotating
          </div>
        )}
      </div>
    </div>
  );
};

export default TVKioskDashboard;
